// ABOUTME: One card of the Actions step: the action kind, its field(s) and languages, and the inputs of that kind.
// ABOUTME: Covers set / clear / copy with their write options, the replace-text pattern, the link pickers, the AI translation and the delete.

import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    DeleteIcon,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@ds/coolshop';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import convert from 'convert-units';
import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    ACTION_KINDS,
    ACTION_TYPES,
    BulkChangeAction,
    BulkChangeActionKind,
    DeleteNodeAction,
    EligibleContentType,
    FieldAction,
    InheritedValues,
    ReplaceTextAction,
    SetValueAction,
    TranslationOverwrite,
    WriteMode,
} from '../types';
import { ALL_TEXT_FIELDS, defaultAction, isAllTextFields } from '../utils/actionDrafts';
import { actionKindMessages } from '../utils/actionMessages';
import { buildFindPattern, isFindPatternError } from '../utils/buildFindPattern';
import { contentTypeKey, ContentTypeLabeler } from '../utils/contentTypeLabels';
import { fromDateInputValue, toDateInputValue } from '../utils/valueDisplay';
import { InfoNote } from './InfoNote';
import { NodeMultiPicker, NodePicker } from './NodePicker';
import { TryItBox } from './TryItBox';

type FindToggle = 'caseSensitive' | 'wholeWord' | 'isRegex';

/** The translate overwrite option's select value for "absent": only empty languages. */
const ONLY_EMPTY = 'onlyEmpty';

/** The units of a measure kind, as the node editor's unit select lists them. */
const measureUnits = (kind: string | null): string[] => {
    if (!kind) return [];
    try {
        return convert().possibilities(kind as convert.Measure);
    } catch {
        return [];
    }
};

type ActionCardProps = {
    index: number;
    action: BulkChangeAction;
    onChange: (action: BulkChangeAction) => void;
    onRemove: () => void;
    contentTypes: EligibleContentType[];
    locales: string[];
    /** The languages the user may machine-translate into: none hides the translate kind. */
    translatableLocales: string[];
    /** The object schema forbids deleting nodes: hides the delete kind. */
    deleteDisabled: boolean;
    labeler: ContentTypeLabeler;
    /** Another action writes the same field and language. */
    duplicate: boolean;
};

const fieldKey = (ctg: string, ct: string) => (ctg && ct ? contentTypeKey(ctg, ct) : '');

const splitKey = (key: string) => {
    const [ctg = '', ct = ''] = key.split('/');
    return { ctg, ct };
};

function KindSelect({
    value,
    translatableLocales,
    deleteDisabled,
    onChange,
}: {
    value: BulkChangeActionKind;
    translatableLocales: string[];
    deleteDisabled: boolean;
    onChange: (kind: BulkChangeActionKind) => void;
}) {
    const { formatMessage } = useIntl();
    return (
        <Select
            label={formatMessage({ id: 'bulkChange.action.kind', defaultMessage: 'Action' })}
            value={value}
            onChange={e => onChange(e.target.value as BulkChangeActionKind)}
            size="small"
            fullWidth
        >
            {ACTION_KINDS.filter(
                kind =>
                    value === kind ||
                    (kind === 'translate' && translatableLocales.length > 0) ||
                    (kind === 'deleteNode' && !deleteDisabled) ||
                    (kind !== 'translate' && kind !== 'deleteNode'),
            ).map(kind => (
                <MenuItem key={kind} value={kind}>
                    {formatMessage(actionKindMessages[kind])}
                </MenuItem>
            ))}
        </Select>
    );
}

/** The card around an action: its number and the remove button. */
function ActionCardFrame({
    index,
    onRemove,
    children,
}: {
    index: number;
    onRemove: () => void;
    children: ReactNode;
}) {
    const { formatMessage } = useIntl();
    return (
        <Box
            bgcolor="background.paper"
            border={1}
            borderColor="divider"
            borderRadius={2}
            data-testid={`bulk-change-action-${index}`}
        >
            <Stack gap={2} p={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">
                        {formatMessage(
                            { id: 'bulkChange.action.title', defaultMessage: 'Action {number}' },
                            { number: index + 1 },
                        )}
                    </Typography>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={onRemove}
                        aria-label={formatMessage(
                            {
                                id: 'bulkChange.action.remove',
                                defaultMessage: 'Remove action {number}',
                            },
                            { number: index + 1 },
                        )}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Stack>
                {children}
            </Stack>
        </Box>
    );
}

/** The delete action: no field, only what happens to the objects linking to the deleted ones. */
function DeleteActionCard({
    index,
    action,
    onChange,
    onRemove,
    translatableLocales,
    deleteDisabled,
}: Omit<ActionCardProps, 'action'> & { action: DeleteNodeAction }) {
    const { formatMessage } = useIntl();
    return (
        <ActionCardFrame index={index} onRemove={onRemove}>
            <Stack direction="row" gap={2} alignItems="flex-end">
                <Box width={180} flexShrink={0}>
                    <KindSelect
                        value={action.kind}
                        translatableLocales={translatableLocales}
                        deleteDisabled={deleteDisabled}
                        onChange={kind => onChange(defaultAction(kind, action))}
                    />
                </Box>
                <Box width={280} flexShrink={0}>
                    <Select
                        label={formatMessage({
                            id: 'bulkChange.action.linkedAction',
                            defaultMessage: 'Objects linking to them',
                        })}
                        value={action.linkedAction}
                        onChange={e =>
                            onChange({
                                ...action,
                                linkedAction: e.target.value as DeleteNodeAction['linkedAction'],
                            })
                        }
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="unlink">
                            {formatMessage({
                                id: 'bulkChange.action.linkedAction.unlink',
                                defaultMessage: 'Unlink them',
                            })}
                        </MenuItem>
                        <MenuItem value="cascade">
                            {formatMessage({
                                id: 'bulkChange.action.linkedAction.cascade',
                                defaultMessage: 'Delete them too',
                            })}
                        </MenuItem>
                    </Select>
                </Box>
            </Stack>
            <InfoNote
                title={formatMessage({
                    id: 'bulkChange.action.deleteNode.warningTitle',
                    defaultMessage: 'The deletion cannot be undone',
                })}
                text={formatMessage(
                    {
                        id: 'bulkChange.action.deleteNode.warning',
                        defaultMessage:
                            'Every object in scope is deleted with all its descendants, including the ones outside the scope.{linkedAction, select, cascade { The objects linking to them are deleted too, in other hierarchies as well.} other {}}',
                    },
                    { linkedAction: action.linkedAction },
                )}
            />
        </ActionCardFrame>
    );
}

export function ActionCard(props: ActionCardProps) {
    const { action } = props;
    if (action.kind === 'deleteNode') return <DeleteActionCard {...props} action={action} />;
    return <FieldActionCard {...props} action={action} />;
}

function FieldActionCard({
    index,
    action,
    onChange,
    onRemove,
    contentTypes,
    locales,
    translatableLocales,
    deleteDisabled,
    labeler,
    duplicate,
}: Omit<ActionCardProps, 'action'> & { action: FieldAction }) {
    const intl = useIntl();
    const { formatMessage } = intl;

    const target = labeler.contentType(action.ctg, action.ct);
    const fieldLabel = (contentType: EligibleContentType) =>
        `${labeler.groupLabel(contentType.ctg)} › ${labeler.typeLabel(
            contentType.ctg,
            contentType.ct,
        )}`;

    // The fields this kind may write.
    const targetFields = useMemo(() => {
        const sourceType =
            action.kind === 'copyValue'
                ? labeler.contentType(action.from.ctg, action.from.ct)?.type
                : undefined;
        return contentTypes.filter(
            contentType =>
                contentType.editable &&
                ACTION_TYPES[action.kind].includes(contentType.type) &&
                (!sourceType || contentType.type === sourceType) &&
                // A translation writes the other languages of a multilingual field.
                (action.kind !== 'translate' || contentType.multiLanguage),
        );
    }, [contentTypes, action, labeler]);

    // A copy reads its source with view access only: read-only fields are valid sources.
    const sourceFields = useMemo(
        () => contentTypes.filter(contentType => ACTION_TYPES.copyValue.includes(contentType.type)),
        [contentTypes],
    );

    const changeKind = (kind: BulkChangeActionKind) =>
        onChange(defaultAction(kind, action, target?.type));

    const fieldSelect = (
        label: string,
        value: string,
        fields: EligibleContentType[],
        onSelect: (key: string) => void,
        extraOptions: { key: string; label: string }[] = [],
    ) => (
        <Select
            label={label}
            value={value}
            onChange={e => onSelect(e.target.value)}
            size="small"
            fullWidth
        >
            {extraOptions.map(option => (
                <MenuItem key={option.key} value={option.key}>
                    {option.label}
                </MenuItem>
            ))}
            {fields.map(contentType => (
                <MenuItem
                    key={contentTypeKey(contentType.ctg, contentType.ct)}
                    value={contentTypeKey(contentType.ctg, contentType.ct)}
                >
                    {fieldLabel(contentType)}
                </MenuItem>
            ))}
        </Select>
    );

    // A non-localised field is read and written in the default language only.
    const localised = isAllTextFields(action) || !target || target.multiLanguage;
    const translateLanguagesInput = action.kind === 'translate' && (
        <Autocomplete<string, true>
            multiple
            options={translatableLocales}
            getOptionLabel={code => labeler.languageLabel(code)}
            value={action.locales}
            onChange={(_event, next) => onChange({ ...action, locales: next })}
            label={formatMessage({
                id: 'bulkChange.action.targetLanguages',
                defaultMessage: 'Target languages',
            })}
            size="small"
            fullWidth
        />
    );
    const languagesInput =
        translateLanguagesInput ||
        (localised ? (
            <Autocomplete<string, true>
                multiple
                options={locales}
                getOptionLabel={code => labeler.languageLabel(code)}
                value={action.locales ?? []}
                onChange={(_event, next) =>
                    onChange({
                        ...action,
                        locales: next.length ? next : undefined,
                    } as BulkChangeAction)
                }
                label={formatMessage({
                    id: 'bulkChange.action.languages',
                    defaultMessage: 'Languages',
                })}
                placeholder={
                    action.locales?.length
                        ? undefined
                        : formatMessage({
                              id: 'bulkChange.allLanguages',
                              defaultMessage: 'All languages',
                          })
                }
                size="small"
                fullWidth
            />
        ) : null);

    const writeOptions = (current: { writeMode: WriteMode; inheritedValues: InheritedValues }) => (
        <>
            <Stack direction="row" gap={2}>
                <Box width={240} flexShrink={0}>
                    <Select
                        label={formatMessage({
                            id: 'bulkChange.action.writeMode',
                            defaultMessage: 'Write',
                        })}
                        value={current.writeMode}
                        onChange={e =>
                            onChange({
                                ...action,
                                writeMode: e.target.value as WriteMode,
                            } as BulkChangeAction)
                        }
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="onlyIfEmpty">
                            {formatMessage({
                                id: 'bulkChange.action.writeMode.onlyIfEmpty',
                                defaultMessage: 'Only where empty',
                            })}
                        </MenuItem>
                        <MenuItem value="overwrite">
                            {formatMessage({
                                id: 'bulkChange.action.writeMode.overwrite',
                                defaultMessage: 'Overwrite existing values',
                            })}
                        </MenuItem>
                    </Select>
                </Box>
                <Box width={280} flexShrink={0}>
                    <Select
                        label={formatMessage({
                            id: 'bulkChange.action.inheritedValues',
                            defaultMessage: 'Inherited values count as',
                        })}
                        value={current.inheritedValues}
                        onChange={e =>
                            onChange({
                                ...action,
                                inheritedValues: e.target.value as InheritedValues,
                            } as BulkChangeAction)
                        }
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="filled">
                            {formatMessage({
                                id: 'bulkChange.action.inheritedValues.filled',
                                defaultMessage: 'Filled (keep inheritance)',
                            })}
                        </MenuItem>
                        <MenuItem value="empty">
                            {formatMessage({
                                id: 'bulkChange.action.inheritedValues.empty',
                                defaultMessage: 'Empty (write own value)',
                            })}
                        </MenuItem>
                    </Select>
                </Box>
            </Stack>
            {(current.writeMode === 'overwrite' || current.inheritedValues === 'empty') && (
                <Alert severity="warning" data-testid={`bulk-change-action-warning-${index}`}>
                    {formatMessage({
                        id: 'bulkChange.action.overwriteWarning',
                        defaultMessage:
                            'Overwrite replaces values typed by hand, and "Empty" gives inheriting objects their own value, cutting the inheritance.',
                    })}
                </Alert>
            )}
        </>
    );

    const renderSetValue = (setAction: SetValueAction) => {
        const valueLabel = formatMessage({
            id: 'bulkChange.action.value',
            defaultMessage: 'Value',
        });
        const setValue = (value: unknown) => onChange({ ...setAction, value });
        const raw = setAction.value;
        switch (target?.type) {
            case 'select':
                return (
                    <Select
                        label={valueLabel}
                        value={typeof raw === 'string' ? raw : ''}
                        onChange={e => setValue(e.target.value)}
                        size="small"
                        fullWidth
                    >
                        {target.options.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {labeler.optionLabel(target.ctg, target.ct, option.value)}
                            </MenuItem>
                        ))}
                    </Select>
                );
            case 'boolean':
                // Stored as a JSON boolean, as the node editor's checkbox writes it.
                return (
                    <Select
                        label={valueLabel}
                        value={typeof raw === 'boolean' ? String(raw) : ''}
                        onChange={e => setValue(e.target.value === 'true')}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="true">
                            {formatMessage({
                                id: 'bulkChange.boolean.true',
                                defaultMessage: 'Yes',
                            })}
                        </MenuItem>
                        <MenuItem value="false">
                            {formatMessage({
                                id: 'bulkChange.boolean.false',
                                defaultMessage: 'No',
                            })}
                        </MenuItem>
                    </Select>
                );
            case 'dateRange':
                // Stored as "YYYY-M-D", as the node editor's date field writes it.
                return (
                    <TextField
                        label={valueLabel}
                        type="date"
                        value={typeof raw === 'string' ? toDateInputValue(raw) : ''}
                        onChange={e => setValue(fromDateInputValue(e.target.value))}
                        size="small"
                        fullWidth
                    />
                );
            case 'measure': {
                const measure =
                    raw && typeof raw === 'object'
                        ? (raw as { value?: string; measure?: string })
                        : { value: '', measure: '' };
                // The units the node editor offers for the field's kind.
                const units = measureUnits(target?.kind ?? null);
                return (
                    <Stack direction="row" gap={2}>
                        <TextField
                            label={valueLabel}
                            type="number"
                            value={measure.value ?? ''}
                            onChange={e => setValue({ ...measure, value: e.target.value })}
                            size="small"
                            fullWidth
                        />
                        <Box minWidth={160}>
                            <Select
                                label={formatMessage({
                                    id: 'bulkChange.action.unit',
                                    defaultMessage: 'Unit',
                                })}
                                value={measure.measure ?? ''}
                                onChange={e =>
                                    setValue({ ...measure, measure: e.target.value as string })
                                }
                                size="small"
                                fullWidth
                            >
                                {units.map(unit => (
                                    <MenuItem key={unit} value={unit}>
                                        {unit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Stack>
                );
            }
            default:
                return (
                    <TextField
                        label={valueLabel}
                        type={target?.type === 'number' ? 'number' : 'text'}
                        multiline={target?.type === 'html'}
                        value={typeof raw === 'string' ? raw : ''}
                        onChange={e => setValue(e.target.value)}
                        size="small"
                        fullWidth
                    />
                );
        }
    };

    const renderReplaceText = (replaceAction: ReplaceTextAction) => {
        const pattern = buildFindPattern(replaceAction);
        const patternError =
            replaceAction.find && isFindPatternError(pattern) ? pattern.error : null;
        const regex = isFindPatternError(pattern) ? null : pattern.regex;
        const activeToggles = (['caseSensitive', 'wholeWord', 'isRegex'] as FindToggle[]).filter(
            toggle => replaceAction[toggle],
        );
        const toggle = (value: FindToggle, text: string, label: string) => (
            <ToggleButton
                value={value}
                data-testid={`find-replace-toggle-${value}`}
                sx={{ textTransform: 'none' }}
                aria-label={label}
                title={label}
            >
                {text}
            </ToggleButton>
        );
        return (
            <Stack gap={1}>
                <Stack direction="row" gap={1} alignItems="flex-start">
                    <Box flex={1}>
                        <TextField
                            data-testid="find-replace-find-input"
                            label={formatMessage({
                                id: 'findReplace.define.find',
                                defaultMessage: 'Find',
                            })}
                            value={replaceAction.find}
                            onChange={e => onChange({ ...replaceAction, find: e.target.value })}
                            error={!!patternError}
                            helperText={patternError ?? undefined}
                            size="small"
                            fullWidth
                        />
                    </Box>
                    <ToggleButtonGroup
                        size="small"
                        value={activeToggles}
                        onChange={(_event, next: FindToggle[]) =>
                            onChange({
                                ...replaceAction,
                                caseSensitive: next.includes('caseSensitive'),
                                wholeWord: next.includes('wholeWord'),
                                isRegex: next.includes('isRegex'),
                            })
                        }
                    >
                        {toggle(
                            'caseSensitive',
                            'Aa',
                            formatMessage({
                                id: 'findReplace.define.caseSensitive',
                                defaultMessage: 'Match case',
                            }),
                        )}
                        {toggle(
                            'wholeWord',
                            'ab',
                            formatMessage({
                                id: 'findReplace.define.wholeWord',
                                defaultMessage: 'Match whole word',
                            }),
                        )}
                        {toggle(
                            'isRegex',
                            '.*',
                            formatMessage({
                                id: 'findReplace.define.isRegex',
                                defaultMessage: 'Use regular expression',
                            }),
                        )}
                    </ToggleButtonGroup>
                </Stack>
                <TextField
                    data-testid="find-replace-replace-input"
                    label={formatMessage({
                        id: 'findReplace.define.replace',
                        defaultMessage: 'Replace with',
                    })}
                    value={replaceAction.replace}
                    onChange={e => onChange({ ...replaceAction, replace: e.target.value })}
                    helperText={formatMessage(
                        {
                            id: 'findReplace.define.replaceHint',
                            defaultMessage:
                                'Leave empty to delete the matches. With a regular expression use {group} or {namedGroup} for a captured group, {wholeMatch} for the whole match, {dollar} for a literal $.',
                        },
                        { group: '$1', namedGroup: '$<name>', wholeMatch: '$&', dollar: '$$' },
                    )}
                    size="small"
                    fullWidth
                />
                <TryItBox regex={regex} replace={replaceAction.replace} />
            </Stack>
        );
    };

    const kindSelect = (
        <KindSelect
            value={action.kind}
            translatableLocales={translatableLocales}
            deleteDisabled={deleteDisabled}
            onChange={changeKind}
        />
    );

    const fieldLabelText = formatMessage({
        id: 'bulkChange.action.field',
        defaultMessage: 'Field',
    });
    const allTextFieldsOption = {
        key: contentTypeKey(ALL_TEXT_FIELDS, ALL_TEXT_FIELDS),
        label: formatMessage({
            id: 'bulkChange.action.allTextFields',
            defaultMessage: 'All text fields',
        }),
    };
    const allMultilingualFieldsOption = {
        key: contentTypeKey(ALL_TEXT_FIELDS, ALL_TEXT_FIELDS),
        label: formatMessage({
            id: 'bulkChange.action.allMultilingualFields',
            defaultMessage: 'All multilingual fields',
        }),
    };
    const wildcardOptions = () => {
        if (action.kind === 'replaceText') return [allTextFieldsOption];
        if (action.kind === 'translate') return [allMultilingualFieldsOption];
        return [];
    };

    const linkHierarchy = target?.hierarchy ?? null;
    const isLinkSetAction = action.kind === 'addLinks' || action.kind === 'removeLinks';
    const linkSetPicker = isLinkSetAction && (
        <NodeMultiPicker
            hierarchyId={linkHierarchy}
            label={
                action.kind === 'addLinks'
                    ? formatMessage({
                          id: 'bulkChange.action.add',
                          defaultMessage: 'Add',
                      })
                    : formatMessage({
                          id: 'bulkChange.action.removeNodes',
                          defaultMessage: 'Remove',
                      })
            }
            value={action.nodeIds}
            onChange={nodeIds => onChange({ ...action, nodeIds })}
        />
    );

    const firstRow =
        action.kind === 'copyValue' ? (
            <Stack direction="row" gap={2} alignItems="flex-end">
                <Box width={160} flexShrink={0}>
                    {kindSelect}
                </Box>
                <Box width={170} flexShrink={0}>
                    {fieldSelect(
                        formatMessage({
                            id: 'bulkChange.action.fromField',
                            defaultMessage: 'From field',
                        }),
                        fieldKey(action.from.ctg, action.from.ct),
                        sourceFields,
                        key => {
                            const source = splitKey(key);
                            const sourceType = labeler.contentType(source.ctg, source.ct)?.type;
                            // The target must have the type of the source.
                            const keepsTarget = target && target.type === sourceType;
                            onChange({
                                ...action,
                                from: { ...action.from, ...source },
                                ...(keepsTarget ? {} : { ctg: '', ct: '' }),
                            });
                        },
                    )}
                </Box>
                <Box width={170} flexShrink={0}>
                    {fieldSelect(
                        formatMessage({
                            id: 'bulkChange.action.toField',
                            defaultMessage: 'To field',
                        }),
                        fieldKey(action.ctg, action.ct),
                        targetFields,
                        key => onChange({ ...action, ...splitKey(key) }),
                    )}
                </Box>
                <Box flex={1} minWidth={0}>
                    {languagesInput}
                </Box>
            </Stack>
        ) : (
            <Stack direction="row" gap={2} alignItems="flex-end">
                <Box width={180} flexShrink={0}>
                    {kindSelect}
                </Box>
                <Box width={240} flexShrink={0}>
                    {fieldSelect(
                        fieldLabelText,
                        fieldKey(action.ctg, action.ct),
                        targetFields,
                        key => onChange({ ...action, ...splitKey(key) } as BulkChangeAction),
                        wildcardOptions(),
                    )}
                </Box>
                <Box flex={1} minWidth={0}>
                    {isLinkSetAction
                        ? linkSetPicker
                        : action.kind !== 'replaceLink' && languagesInput}
                </Box>
            </Stack>
        );

    return (
        <ActionCardFrame index={index} onRemove={onRemove}>
            {firstRow}

            {action.kind === 'copyValue' && localised && (
                <Box maxWidth={240}>
                    <Select
                        label={formatMessage({
                            id: 'bulkChange.action.fromLanguage',
                            defaultMessage: 'Copy from language',
                        })}
                        value={action.from.lang ?? ''}
                        onChange={e =>
                            onChange({
                                ...action,
                                from: { ...action.from, lang: e.target.value || undefined },
                            })
                        }
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">
                            {formatMessage({
                                id: 'bulkChange.action.sameLanguage',
                                defaultMessage: 'The same language',
                            })}
                        </MenuItem>
                        {locales.map(locale => (
                            <MenuItem key={locale} value={locale}>
                                {labeler.languageLabel(locale)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {action.kind === 'setValue' && renderSetValue(action)}
            {action.kind === 'replaceText' && renderReplaceText(action)}
            {action.kind === 'replaceLink' && (
                <Stack direction="row" gap={2}>
                    <Box flex={1}>
                        <NodePicker
                            hierarchyId={linkHierarchy}
                            label={formatMessage({
                                id: 'bulkChange.action.replaceLinkFrom',
                                defaultMessage: 'Replace',
                            })}
                            value={action.fromNodeId || null}
                            onChange={id => onChange({ ...action, fromNodeId: id ?? '' })}
                        />
                    </Box>
                    <Box flex={1}>
                        <NodePicker
                            hierarchyId={linkHierarchy}
                            label={formatMessage({
                                id: 'bulkChange.action.replaceLinkTo',
                                defaultMessage: 'With',
                            })}
                            value={action.toNodeId || null}
                            onChange={id => onChange({ ...action, toNodeId: id ?? '' })}
                        />
                    </Box>
                </Stack>
            )}
            {(action.kind === 'setValue' || action.kind === 'copyValue') && writeOptions(action)}

            {action.kind === 'translate' && (
                <Box width={420}>
                    <Select
                        label={formatMessage({
                            id: 'bulkChange.action.existingTranslations',
                            defaultMessage: 'Existing translations',
                        })}
                        value={action.overwrite ?? ONLY_EMPTY}
                        onChange={e =>
                            onChange({
                                ...action,
                                overwrite:
                                    e.target.value === ONLY_EMPTY
                                        ? undefined
                                        : (e.target.value as TranslationOverwrite),
                            })
                        }
                        size="small"
                        fullWidth
                    >
                        <MenuItem value={ONLY_EMPTY}>
                            {formatMessage({
                                id: 'bulkChange.action.existingTranslations.onlyEmpty',
                                defaultMessage: 'Keep them: translate only where empty',
                            })}
                        </MenuItem>
                        <MenuItem value="unapproved">
                            {formatMessage({
                                id: 'bulkChange.action.existingTranslations.unapproved',
                                defaultMessage: 'Overwrite unapproved machine translations',
                            })}
                        </MenuItem>
                        <MenuItem value="all">
                            {formatMessage({
                                id: 'bulkChange.action.existingTranslations.all',
                                defaultMessage: 'Overwrite all translations',
                            })}
                        </MenuItem>
                    </Select>
                </Box>
            )}

            {target?.repeatable && (
                <FormControlLabel
                    control={
                        <Checkbox
                            id={`bulk-change-all-groups-${index}`}
                            checked={!!action.allGroupIndexes}
                            onChange={e =>
                                onChange({
                                    ...action,
                                    allGroupIndexes: e.target.checked || undefined,
                                })
                            }
                        />
                    }
                    label={formatMessage({
                        id: 'bulkChange.action.allGroupIndexes',
                        defaultMessage: 'Every repetition of the group (not only the first)',
                    })}
                />
            )}

            {duplicate && (
                <Alert severity="error" data-testid={`bulk-change-action-duplicate-${index}`}>
                    {formatMessage({
                        id: 'bulkChange.action.duplicate',
                        defaultMessage:
                            'Another action changes the same field in the same language: each field and language can be changed by one action only.',
                    })}
                </Alert>
            )}
        </ActionCardFrame>
    );
}
